import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtSize } from "@/lib/format";
import { LogoMark } from "@/components/Logo";
import { FileText, Download, Clock, MapPin, ShieldCheck, Camera } from "lucide-react";

export default function SharedProperty() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/public/share/${token}`)
      .then(({ data }) => setData(data))
      .catch((err) => setError(err.response?.data?.detail || "This link is not valid."));
  }, [token]);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-background">
        <div className="surface p-10 max-w-md text-center" data-testid="share-error">
          <ShieldCheck size={26} className="mx-auto text-muted-foreground" />
          <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">Link unavailable</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <p className="mt-4 text-xs text-muted-foreground">
            Please ask your contact at the company for a fresh link.
          </p>
        </div>
      </div>
    );

  if (!data) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;

  const p = data.property;
  const groups = data.documents.reduce((acc, d) => {
    (acc[d.group] = acc[d.group] || []).push(d);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background" data-testid="share-page">
      <header className="bg-zinc-950 text-white">
        <div className="max-w-4xl mx-auto px-5 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoMark size={30} />
            <div className="leading-none">
              <p className="font-display font-extrabold tracking-tighter">REAM</p>
              <p className="label-tiny mt-1 text-zinc-500">Read-only document share</p>
            </div>
          </div>
          <span className="hidden sm:flex items-center gap-2 text-xs text-zinc-400" data-testid="share-expiry">
            <Clock size={13} /> Valid until {fmtDate(data.expires_at)}
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-8 rise">
        <p className="label-tiny">{p.asset_category} property</p>
        <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter"
          data-testid="share-property-name">
          {p.name}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground flex items-center gap-1.5">
          <MapPin size={14} /> {[p.village, p.mandal, p.district, p.state, p.pincode].filter(Boolean).join(", ")}
        </p>
        {data.label && (
          <p className="mt-4 text-sm">
            Shared with <span className="font-semibold">{data.label}</span>
            {data.shared_by ? ` by ${data.shared_by}` : ""}.
          </p>
        )}
        <p className="mt-1 text-xs text-muted-foreground sm:hidden">
          Valid until {fmtDate(data.expires_at)}
        </p>

        <div className="mt-6 surface grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-border overflow-hidden">
          {[
            ["Survey no.", p.survey_number],
            ["Extent", `${p.extent_acres || 0} ac`],
            ["Khata / Passbook", p.khata_number],
            ["Owner", p.owner_name],
          ].map(([l, v]) => (
            <div key={l} className="p-4">
              <p className="label-tiny">{l}</p>
              <p className="mt-1.5 text-sm font-semibold break-words">{v || "—"}</p>
            </div>
          ))}
        </div>

        {Object.entries(groups).map(([group, docs]) => (
          <section key={group} className="mt-8" data-testid={`share-group-${group.replace(/\s+/g, "-")}`}>
            <p className="label-tiny">{group}</p>
            <div className="mt-3 surface divide-y divide-border">
              {docs.map((d) => (
                <a key={d.id} href={`${API}/public/share/${token}/files/${d.id}`}
                  target="_blank" rel="noreferrer" data-testid={`share-doc-${d.id}`}
                  className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors duration-150">
                  <FileText size={18} className="text-primary shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{d.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {d.doc_type}
                      {d.doc_date ? ` · ${fmtDate(d.doc_date)}` : ""}
                      {d.reference_no ? ` · Ref ${d.reference_no}` : ""} · {fmtSize(d.size)}
                    </p>
                  </div>
                  <Download size={15} className="text-muted-foreground shrink-0" />
                </a>
              ))}
            </div>
          </section>
        ))}

        {data.documents.length === 0 && (
          <p className="mt-8 surface p-8 text-sm text-muted-foreground text-center" data-testid="share-empty">
            No documents have been shared on this link.
          </p>
        )}

        {data.visits?.length > 0 && (
          <section className="mt-8" data-testid="share-visits">
            <p className="label-tiny">Recent site inspections</p>
            <div className="mt-3 surface divide-y divide-border">
              {data.visits.map((v, i) => (
                <div key={i} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div>
                    <p className="text-sm font-semibold">{fmtDateTime(v.captured_at)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                      <Camera size={12} /> {v.media_count} photo/video · {v.observation}
                    </p>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">
                    {v.latitude != null ? `${v.latitude.toFixed(4)}, ${v.longitude.toFixed(4)}` : "no geotag"}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        <p className="mt-10 text-xs text-muted-foreground">
          This is a read-only view generated by REAM. The link stops working after{" "}
          {fmtDate(data.expires_at)} or if it is revoked earlier.
        </p>
      </main>
    </div>
  );
}
